import {
  ActivityStatus,
  CommonDataModel,
  DealPartner,
  DealType,
  DspAccountsContainer,
  ExchangeDeal,
  Personnel,
  ResponseFromServer,
  JapiQuery,
  JapiTableFilter,
} from '@/app/shared/_models/models';
import { Injectable } from '@angular/core';
import { ParsedRequestParams, RequestQueryBuilder } from '@libs/crud-request-nestjs';
import { Observable } from 'rxjs';

import { ApiService } from '../../../shared/_services/api.service';
import { UniversalMediaList, UniversalMediaListAssociatedDealsRemovalPayload, UniversalMediaListDomainUpdatePayload } from '../models';

@Injectable({ providedIn: 'root' })
export class UniversalMediaListsService {
  constructor(private readonly api: ApiService) {}

  getUniversalMediaLists(parsedRequestParams: Partial<ParsedRequestParams>): Observable<CommonDataModel<UniversalMediaList[]>> {
    const filters = this.getFilters(parsedRequestParams);
    return this.api.getUniversalMediaLists(filters);
  }

  getUniversalMediaList(id: number): Observable<ResponseFromServer<UniversalMediaList>> {
    const parsedRequestParams: Partial<ParsedRequestParams> = { join: [{ field: 'segment' }] };

    const filters = this.getFilters(parsedRequestParams);
    return this.api.getUniversalMediaList(id, filters);
  }

  archiveUniversalMediaList(id: number): Observable<ResponseFromServer<void>> {
    return this.api.deleteUniversalMediaList(id);
  }

  restoreUniversalMediaList(id: number): Observable<ResponseFromServer<void>> {
    return this.api.restoreUniversalMediaList(id);
  }

  getUniversalMediaListDomains(id: number, parsedRequestParams: Partial<ParsedRequestParams> = {}): Observable<CommonDataModel<string[]>> {
    parsedRequestParams.page ??= 1;
    parsedRequestParams.limit ??= 10;
    parsedRequestParams.join = [{ field: 'segment.segmentMaps' }];

    const filters = this.getFilters(parsedRequestParams);
    return this.api.getUniversalMediaListDomains(id, filters);
  }

  updateUniversalMediaListDomains(
    id: number,
    universalMediaListDomainRemovalPayload: UniversalMediaListDomainUpdatePayload
  ): Observable<void> {
    return this.api.updateUniversalMediaListDomains(id, universalMediaListDomainRemovalPayload);
  }

  removeUniversalMediaListAssociatedDeals(
    id: number,
    universalMediaListAssociatedDealsRemovalPayload: UniversalMediaListAssociatedDealsRemovalPayload
  ): Observable<ResponseFromServer<UniversalMediaList>> {
    return this.api.deleteUniversalMediaListAssociatedDeals(id, universalMediaListAssociatedDealsRemovalPayload);
  }

  downloadUniversalMediaListDomains(id: number): Observable<unknown> {
    return this.api.downloadUniversalMediaListDomains(id);
  }

  createUniversalMediaList(universalMediaList: UniversalMediaList): Observable<ResponseFromServer<UniversalMediaList>> {
    return this.api.createUniversalMediaList(universalMediaList);
  }

  updateUniversalMediaList(id: number, universalMediaList: UniversalMediaList): Observable<ResponseFromServer<UniversalMediaList>> {
    return this.api.updateUniversalMediaList(id, universalMediaList);
  }

  getExchangeDeals(searchValue?: string): Observable<CommonDataModel<ExchangeDeal[]>> {
    const parsedRequestParams: Partial<ParsedRequestParams> = {
      fields: ['dealId', 'externalDealId', 'name', 'description'],
      search: {
        status: { $in: [ActivityStatus.ACTIVE, ActivityStatus.INACTIVE] },
      }
    };

    if (searchValue) {
      parsedRequestParams.search.$or = [
        { externalDealId: { $contL: `%${searchValue}%` } },
        { name: { $contL: `%${searchValue}%` } },
        { description: { $contL: `%${searchValue}%` } }
      ];
    }

    const filters = this.getFilters(parsedRequestParams);
    return this.api.getExchangeDeals(filters);
  }

  getPartnerAccounts(parsedRequestParams?: Partial<ParsedRequestParams>): Observable<CommonDataModel<DealPartner[]>> {
    parsedRequestParams ??= { limit: 1000, sort: [{ field: 'name', order: 'ASC' }] };

    const filters = this.getFilters(parsedRequestParams);
    return this.api.getDealPartnerAccounts(filters);
  }

  getAssociatedDeals(parsedRequestParams: Partial<ParsedRequestParams>): Observable<CommonDataModel<ExchangeDeal[]>> {
    const filters = this.getFilters(parsedRequestParams);
    return this.api.getExchangeDeals(filters);
  }

  getAssociatedDealsPersonnel(): Observable<ResponseFromServer<Personnel[]>> {
    return this.api.getExchangePersonnel();
  }

  getDealTypes(): Observable<ResponseFromServer<DealType[]>> {
    return this.api.getExchangeDealTypes();
  }

  getDspAccounts(): Observable<ResponseFromServer<DspAccountsContainer>> {
    return this.api.getExchangeDspAccounts();
  }

  duplicateUniversalMediaList(id: number): Observable<ResponseFromServer<UniversalMediaList>> {
    return this.api.duplicateUniversalMediaList(id);
  }


  public buildPublisherSearchQuery(term: string): JapiQuery {
    const filters: JapiTableFilter[] = [{ fieldName: 'status', operation: 'EQUALS', value: 'active' }];
    if (term) {
      const isNum = !isNaN(+term);
      filters.push({ fieldName: isNum ? 'publisherId' : 'publisherName', operation: isNum ? 'EQUALS' : 'LIKE', value: term });
    }
    return { filter: { filters, withTrashed: false }, paging: { number: 1, size: 30 } };
  }

  public buildPublisherBulkQuery(ids: Array<string | number>): JapiQuery {
    const filters: JapiTableFilter[] = [
      { fieldName: 'publisherId', operation: 'IN', value: ids },
      { fieldName: 'status', operation: 'EQUALS', value: 'active' }
    ];
    return { filter: { filters, withTrashed: false } };
  }

  private getFilters(parsedRequestParams?: Partial<ParsedRequestParams>): string {
    if (!parsedRequestParams || Object.keys(parsedRequestParams).length === 0) return '';
    return RequestQueryBuilder.create(parsedRequestParams).query();
  }
}
